import { Component, Input } from '@angular/core';
import { TypeLocationEnum } from 'src/app/enum/decking-boards/type-location.enum';
import { DeckingBoardsService } from '../../../../services/configurators-2d/applications/decking-boards.service';
import { fabric } from 'fabric';
import { ShapePreview } from '../../../../models/configurators-2d/applications/decking-boards/preview/shapePreview';
import { ShapeTypeEnum } from '../../../../enum/shape-type.enum';
import { Position } from '../../../../models/configurators-2d/applications/decking-boards/preview/position';
import { cloneDeep, distinct } from '../../../../services/helpers/utils';
import { FabricPreviewItem } from '../../configurator/previews/fabric-preview-item';
import { Configuration } from '../../../../models/configurators-2d/applications/decking-boards/configuration';
import { generateHTMLImageElement } from '../../../../services/helpers/fabricjs-canvas-helper';

@Component({
  selector: 'ng-decking-boards-preview',
  templateUrl: './preview.component.html',
})
export class DeckingBoardsPreviewComponent extends FabricPreviewItem<Configuration, DeckingBoardsService> {
  @Input() subStep: number;

  @Input() step: number;

  // URL de base des images
  baseImgURL: string = undefined;

  // configuration de la preview
  blue = '#005CCA';
  white = '#fff';
  shapeSize: number;
  canvasMargin = 80;
  letterMargin = 30;

  // canvas shape
  shapes: fabric.Path[] = [];
  shapePreviews: ShapePreview[];
  defaultShapePreviews: ShapePreview[];

  // canvas letters
  letters: fabric.Group[] = [];

  constructor(appService: DeckingBoardsService) {
    super(appService);
  }

  ngOnInit(): void {
    this.baseImgURL = this.service.apiUrl;

    this.defaultShapePreviews = [
      {
        type: ShapeTypeEnum.rectangle,
        svgs: [{ svg: 'M 0 0 L 0 1 L 1 1 L 1 0 Z' }],
      },
      {
        type: ShapeTypeEnum.l,
        svgs: [{ svg: 'M 0 0 L 0 2 L 1 2 L 1 1 L 2 1 L 2 0 Z' }],
      },
      {
        type: ShapeTypeEnum.reversedL,
        svgs: [{ svg: 'M 0 0 L 0 1 L 1 1 L 1 2 L 2 2 L 2 0 Z' }],
      },
      {
        type: ShapeTypeEnum.t,
        svgs: [{ svg: 'M 0 0 L 0 2 L 1 2 L 1 4 L 3 4 L 3 2 L 4 2 L 4 0 Z' }],
      },
      {
        type: ShapeTypeEnum.u,
        svgs: [{ svg: 'M0 0 0 4 1 4 1 2 3 2 3 4 4 4 4 0Z' }],
      },
      {
        type: ShapeTypeEnum.poolOutline,
        svgs: [
          { svg: 'M0 0 0 10 10 10 10 0Z' },
          {
            svg: 'M0 0 0 6 4 6 4 0Z',
            options: { left: 2, top: 1, fill: `${this.baseImgURL}Picture/DeckingBoards/Shapes/Pool.png` },
          },
        ],
      },
    ];
  }

  async update(): Promise<void> {
    await super.update();
    this.computeBackground();
    await this.computeShape();
  }

  override async createOrUpdate(): Promise<void> {
    await super.createOrUpdate();

    // calcul de la taille max d'une forme
    this.shapeSize = Math.min.apply(null, [this.canvas.width, this.canvas.height]) - this.canvasMargin * 2;

    // redimenssione les formes à la taille renseignée
    this.shapePreviews = this.resizeShape();
  }

  /**
   * Mets à jour le background
   */
  private computeBackground(): void {
    let that = this;
    this.canvas.setBackgroundImage(null, this.canvas.renderAll.bind(this.canvas));

    // initialisation du fond
    if (this.configuration.location != null) {
      fabric.Image.fromURL(
        this.baseImgURL + this.configuration.location.picture.path,
        function (locationImage: fabric.Image) {
          that.canvas.setBackgroundImage(locationImage, that.canvas.renderAll.bind(that.canvas), {
            scaleX: that.canvas.width / locationImage.width,
            scaleY: that.canvas.height / locationImage.height,
          });
        },
        { crossOrigin: 'anonymous' }
      );
    }
  }

  /**
   * Créer la forme sélectionnée et les lettres des côtés
   * @private
   */
  private async computeShape(): Promise<void> {
    let that = this;

    if (this.shapes.length > 0) {
      this.shapes.forEach(s => this.canvas.remove(s));
      this.shapes = [];
    }

    if (this.letters.length > 0) {
      this.letters?.forEach(l => this.canvas.remove(l));
      this.letters = [];
    }

    if (this.step >= 2 && this.configuration.shape) {
      let shapePreview = this.shapePreviews.find(sp => sp.type === this.configuration.shape.shapeType);
      for (let i = 0; i < shapePreview.svgs.length; i++) {
        let svg = shapePreview.svgs[i];

        // option côté + this.letterMargin ((taille du canvas - taille de la forme) / 2)
        let left = (svg.options ? svg.options.left : 0) + (that.canvas.width - that.shapeSize) / 2;
        let top = (svg.options ? svg.options.top : 0) + (that.canvas.height - that.shapeSize) / 2;

        let shape = new fabric.Path(svg.svg, {
          stroke: this.isConcrete() ? this.blue : this.white,
          strokeWidth: 2,
          left: left,
          top: top,
          selectable: false,
          evented: false,
          fill: 'transparent',
        });

        let imgPath: string;

        /**
         * Type de pose
         * On l'affiche quand :
         * - On est à l'étape 2 et que le layingType est sélectionné
         * - On est à l'étape 3 et la deckingBoard n'est pas encore sélectionnée
         */
        if ((this.step == 2 && this.configuration.layingType) || (this.step > 2 && !this.configuration.deckingBoard)) {
          imgPath = this.isConcrete()
            ? this.baseImgURL + this.configuration.layingType.concretePicture.path
            : this.baseImgURL + this.configuration.layingType.picture.path;
        }

        /**
         * Choix de la DeckingBoardType
         * On l'affiche quand :
         * - On est à l'étape 3 et la deckingBoard est sélectionnée
         */
        if (this.step > 2 && this.configuration.deckingBoard) {
          imgPath = this.baseImgURL + this.getDeckingBoardPath();
        }

        // que sur l'image de fond
        if (imgPath && i === 0) {
          let image = await generateHTMLImageElement(imgPath);
          shape.set(
            'fill',
            new fabric.Pattern({
              source: image,
              repeat: 'no-repeat',
            })
          );

          that.shapes.push(shape);
          that.canvas.sendToBack(shape);

          that.getPathLines(shapePreview.type, that.shapes.length);
        } else {
          if (svg.options?.fill) {
            let image = await generateHTMLImageElement(svg.options.fill);

            shape.set(
              'fill',
              new fabric.Pattern({
                source: image,
                repeat: 'no-repeat',
              })
            );

            that.shapes.push(shape);
            that.canvas.add(shape);
            that.getPathLines(shapePreview.type, that.shapes.length);
          } else {
            this.shapes.push(shape);
            that.canvas.sendToBack(shape);
            that.getPathLines(shapePreview.type, this.shapes.length);
          }
        }
      }
    }
  }

  /**
   * Calcul les dimensions final du svg
   * @private
   */
  private resizeShape(): ShapePreview[] {
    let shapePreviews: ShapePreview[] = cloneDeep<ShapePreview[]>(this.defaultShapePreviews);
    for (const shapePreview of shapePreviews) {
      for (const svg of shapePreview.svgs) {
        let found = svg.svg.match(/\d+/g);

        // resize du svg
        let numbers = found.map(n => parseInt(n)).filter(n => n > 0);
        numbers = distinct(numbers);
        let maxMargin = svg.options ? Math.max.apply(null, [svg.options.left, svg.options.top]) : 0;
        let max = Math.max.apply(null, numbers) + maxMargin;

        let splitSvg = svg.svg.split(' ');
        for (let i = 0; i < splitSvg.length; i++) {
          let string = splitSvg[i];
          let number = numbers.find(n => n.toString() === string);
          if (number) {
            splitSvg[i] = Math.round(this.shapeSize * (number / max)).toString();
          }
        }
        svg.svg = splitSvg.join(' ');

        // resize des options
        if (svg.options) {
          svg.options.left = this.shapeSize * (svg.options.left / max);
          svg.options.top = this.shapeSize * (svg.options.top / max);
        }
      }
    }

    return shapePreviews;
  }

  /**
   * Récupère les lignes d'un svg pour générer les lettres
   * @param shapeType
   * @param position
   * @private
   */
  private getPathLines(shapeType: ShapeTypeEnum, position: number): void {
    let shapePathsLength = this.shapes[position - 1] ? this.shapes[position - 1].path.length - 2 : 0;
    let previousShapePathsLength = this.shapes[position - 2] ? this.shapes[position - 2].path.length - 2 : 0;
    let firstPosition = position + previousShapePathsLength;

    this.createLetter(shapeType, firstPosition);

    // point
    for (let j = 0; j < shapePathsLength; j++) {
      this.createLetter(shapeType, j + firstPosition + 1);
    }
  }

  /**
   * Génère les lettres
   * @param shapeType
   * @param position
   * @private
   */
  private createLetter(shapeType: ShapeTypeEnum, position: number): void {
    let letter = String.fromCharCode(96 + position).toUpperCase();
    let radius = 14;
    let border = 2;
    let iconRadius = radius + border / 2;

    let letterPosition = this.getLetterPosition(letter, shapeType, iconRadius);

    let circle = new fabric.Circle({
      radius: radius,
      fill: this.blue,
      stroke: this.white,
      strokeWidth: border,
      top: letterPosition.y,
      left: letterPosition.x,
    });

    let letterText = new fabric.Text(letter, {
      fontFamily: 'Calibri',
      fontSize: 20,
      fill: this.white,
      textAlign: 'center',
      originX: 'center',
      originY: 'center',
      top: letterPosition.y + iconRadius,
      left: letterPosition.x + iconRadius,
    });

    let group = new fabric.Group([circle, letterText], {
      selectable: false,
      evented: false,
    });

    this.letters.push(group);
    this.canvas.add(group);
  }

  /**
   * Récupère la position centrale de la lettre
   * @param letter
   * @param shapeType
   * @param iconRadius
   * @private
   */
  private getLetterPosition(letter: string, shapeType: ShapeTypeEnum, iconRadius: number): Position {
    let top = (this.canvas.height - this.shapeSize) / 2 - iconRadius;
    let left = (this.canvas.width - this.shapeSize) / 2 - iconRadius;
    switch (shapeType) {
      case ShapeTypeEnum.rectangle:
        switch (letter) {
          case 'A':
            return { x: left + this.shapeSize / 2, y: top - this.letterMargin };

          case 'B':
            return { x: left + this.shapeSize + this.letterMargin, y: top + this.shapeSize / 2 };

          case 'C':
            return { x: left + this.shapeSize / 2, y: top + this.shapeSize + this.letterMargin };

          case 'D':
            return { x: left - this.letterMargin, y: top + this.shapeSize / 2 };
        }
        break;

      case ShapeTypeEnum.l:
        switch (letter) {
          case 'A':
            return { x: left + this.shapeSize / 2, y: top - this.letterMargin };

          case 'B':
            return { x: left + this.shapeSize + this.letterMargin, y: top + this.shapeSize / 4 };

          case 'C':
            return { x: left + this.shapeSize * 0.75, y: top + this.shapeSize / 2 + this.letterMargin };

          case 'D':
            return { x: left + this.shapeSize / 2 + this.letterMargin, y: top + this.shapeSize * 0.75 };

          case 'E':
            return { x: left + this.shapeSize / 4, y: top + this.shapeSize + this.letterMargin };

          case 'F':
            return { x: left - this.letterMargin, y: top + this.shapeSize / 2 };
        }
        break;

      case ShapeTypeEnum.reversedL:
        switch (letter) {
          case 'A':
            return { x: left + this.shapeSize / 2, y: top - this.letterMargin };

          case 'B':
            return { x: left + this.shapeSize + this.letterMargin, y: top + this.shapeSize / 2 };

          case 'C':
            return { x: left + this.shapeSize * 0.75, y: top + this.shapeSize + this.letterMargin };

          case 'D':
            return { x: left + this.shapeSize / 2 - this.letterMargin, y: top + this.shapeSize * 0.75 };

          case 'E':
            return { x: left + this.shapeSize / 4, y: top + this.shapeSize / 2 + this.letterMargin };

          case 'F':
            return { x: left - this.letterMargin, y: top + this.shapeSize / 4 };
        }
        break;

      case ShapeTypeEnum.t:
        switch (letter) {
          case 'A':
            return { x: left + this.shapeSize / 2, y: top - this.letterMargin };

          case 'B':
            return { x: left + this.shapeSize + this.letterMargin, y: top + this.shapeSize / 4 };

          case 'C':
            return { x: left + (this.shapeSize * 7) / 8, y: top + this.shapeSize / 2 + this.letterMargin };

          case 'D':
            return { x: left + this.shapeSize * 0.75 + this.letterMargin, y: top + this.shapeSize * 0.75 };

          case 'E':
            return { x: left + this.shapeSize / 2, y: top + this.shapeSize + this.letterMargin };

          case 'F':
            return { x: left + this.shapeSize / 4 - this.letterMargin, y: top + this.shapeSize * 0.75 };

          case 'G':
            return { x: left + this.shapeSize / 8, y: top + this.shapeSize / 2 + this.letterMargin };

          case 'H':
            return { x: left - this.letterMargin, y: top + this.shapeSize / 4 };
        }
        break;

      case ShapeTypeEnum.u:
        switch (letter) {
          case 'A':
            return { x: left + this.shapeSize / 2, y: top - this.letterMargin };

          case 'B':
            return { x: left + this.shapeSize + this.letterMargin, y: top + this.shapeSize / 2 };

          case 'C':
            return { x: left + (this.shapeSize * 7) / 8, y: top + this.shapeSize + this.letterMargin };

          case 'D':
            return { x: left + (this.shapeSize * 3) / 4 - this.letterMargin, y: top + (this.shapeSize * 3) / 4 };

          case 'E':
            return { x: left + this.shapeSize / 2, y: top + this.shapeSize / 2 + this.letterMargin };

          case 'F':
            return { x: left + this.shapeSize / 4 + this.letterMargin, y: top + this.shapeSize * 0.75 };

          case 'G':
            return { x: left + this.shapeSize / 8, y: top + this.shapeSize + this.letterMargin };

          case 'H':
            return { x: left - this.letterMargin, y: top + this.shapeSize / 2 };
        }
        break;

      case ShapeTypeEnum.poolOutline:
        switch (letter) {
          case 'A':
            return { x: left + this.shapeSize / 2, y: top - this.letterMargin };

          case 'B':
            return { x: left + this.shapeSize + this.letterMargin, y: top + this.shapeSize / 2 };

          case 'C':
            return { x: left + this.shapeSize / 2, y: top + this.shapeSize + this.letterMargin };

          case 'D':
            return { x: left - this.letterMargin, y: top + this.shapeSize / 2 };

          case 'E':
            return { x: left + this.shapeSize / 2, y: top + this.shapeSize / 7 - this.letterMargin };

          case 'F':
            return { x: left + this.shapeSize / 7 + this.letterMargin, y: top + this.shapeSize / 2 };

          case 'G':
            return { x: left + this.shapeSize / 2, y: top + (this.shapeSize * 6) / 7 + this.letterMargin };

          case 'H':
            return { x: left + (this.shapeSize * 6) / 7 - this.letterMargin, y: top + this.shapeSize / 2 };
        }
        break;
    }
  }

  private getDeckingBoardPath(): string {
    return this.configuration.deckingBoard.deckingBoardConstraints.find(
      c => c.layingTypeId == this.configuration.layingType.id
    ).picture.path;
  }

  private isConcrete() {
    return this.configuration.location.locationType != TypeLocationEnum.dirt;
  }
}
